import { analyticsService } from './analyticsService';
import { logger } from '../logger';
import { getHotelId } from '../supabase/configClient';
import { sessionTracker } from './sessionTracker';

/**
 * Initializes the analytics service and sends initial app load event
 */
export const initAnalytics = async (): Promise<void> => {
  try {
    // Get hotel ID from window.HOTEL_CONFIG or fallback
    const hotelId = getHotelId();
    const hotelSlug = window.HOTEL_CONFIG?.slug || '';
    const baseUrl = window.HOTEL_CONFIG?.baseUrl || '';

    logger.debug('Initializing analytics service', {
      category: 'Analytics',
      data: { 
        timestamp: new Date().toISOString(),
        hotelId,
        hotelSlug,
        baseUrl
      }
    });

    // Update session tracker with hotel info
    sessionTracker.setHotelInfo(hotelId, hotelSlug);

    // Start session tracking
    await sessionTracker.startTracking();

    // Track application start event
    analyticsService.trackEvent({
      event_type: 'session',
      event_name: 'app_start',
      event_data: {
        hotel_id: hotelId,
        hotel_slug: hotelSlug,
        base_url: baseUrl,
        session_id: sessionTracker.getSessionId(),
        anonymous_id: sessionTracker.getAnonymousId(),
        user_agent: navigator.userAgent,
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        referrer: document.referrer || 'direct',
        is_pwa: window.matchMedia('(display-mode: standalone)').matches,
        timestamp: new Date().toISOString()
      }
    });

    // Track initial page view
    analyticsService.trackEvent({
      event_type: 'navigation',
      event_name: 'page_view',
      event_data: {
        path: window.location.pathname,
        page_title: document.title,
        query_params: window.location.search,
        hash: window.location.hash,
        hotel_id: hotelId,
        hotel_slug: hotelSlug,
        base_url: baseUrl,
        session_id: sessionTracker.getSessionId(),
        anonymous_id: sessionTracker.getAnonymousId(),
        timestamp: new Date().toISOString()
      }
    });

    logger.debug('Analytics initialization complete', {
      category: 'Analytics',
      data: { 
        timestamp: new Date().toISOString(),
        hotelId,
        hotelSlug,
        baseUrl
      }
    });

    return Promise.resolve();
  } catch (error) {
    logger.error('Error initializing analytics', {
      category: 'Analytics',
      data: { 
        error,
        errorDetails: error instanceof Error ? {
          message: error.message,
          stack: error.stack
        } : error,
        timestamp: new Date().toISOString()
      }
    });
    return Promise.reject(error);
  }
};